<template>
  <div class="step-harmonisation">
    <v-container fluid class="pa-6 px-4">
      <v-row justify="center">
        <v-col cols="12" lg="11">
          <div v-if="!issues || issues.length === 0" class="text-center py-8">
            <v-icon size="64" color="success" class="mb-4">mdi-check-circle</v-icon>
            <div class="text-h6">All values are consistent</div>
            <div class="text-body-2 grey--text">No spelling variations found</div>
          </div>

          <div v-else>
            <div class="d-flex align-center mb-6 mt-2">
              <div class="text-h6 font-weight-medium">
                <strong>{{ currentIssueIndex + 1 }}</strong> of <strong>{{ issues.length }}</strong>
                to review
              </div>
              <v-spacer></v-spacer>
              <div class="d-flex">
                <v-chip v-for="(issue, idx) in issues" :key="idx" small class="mx-1"
                  :color="getIssueChipColor(issue, idx)" :text-color="idx === currentIssueIndex ? 'white' : undefined"
                  @click="goToIssue(idx)">
                  {{ idx + 1 }}
                </v-chip>
              </div>
            </div>

            <v-alert v-if="currentIssue" type="info" prominent outlined class="mb-4">
              <div class="text-body-2">
                The <strong>"{{ currentIssue.displayColumn }}"</strong> field has values that look the same but are written differently.
                <br><strong>Pick one to use for all</strong>, or enter your own.
              </div>
              <div class="text-body-2 mt-2">
                You can also skip this item and continue without changes.
              </div>
              <div v-if="currentIssue.variants.length > 2" class="text-body-2 mt-3" style="border-top: 1px solid rgba(0,0,0,0.1); padding-top: 12px;">
                If a value is already correct and shouldn't be changed (e.g. a sub-brand), 
                click <strong>Keep as-is</strong> on that option.
              </div>
            </v-alert>

            <div v-if="currentIssue" class="harmonisation-content mb-4">
              <v-radio-group v-model="currentIssue.selectedValue" @change="onValueSelected" class="mt-0">
                <div class="variants-grid">
                  <div 
                    v-for="variant in currentIssue.variants" 
                    :key="variant.value" 
                    class="variant-option"
                    :class="{ 
                      'variant-option--selected': currentIssue.selectedValue === variant.value && !isVariantIgnored(variant),
                      'variant-option--ignored': isVariantIgnored(variant)
                    }"
                  >
                    <div v-if="isVariantIgnored(variant)" class="variant-ignored-content">
                      <div class="d-flex align-center flex-wrap">
                        <v-icon small class="mr-2" color="grey">mdi-cancel</v-icon>
                        <span class="variant-value variant-value--ignored mr-3">"{{ variant.value }}"</span>
                        <v-chip small color="grey lighten-1" text-color="white" class="mr-2">
                          Keeping as-is
                        </v-chip>
                        <span class="grey--text text-caption">({{ variant.count }} row{{ variant.count > 1 ? 's' : '' }})</span>
                      </div>
                      <div class="text-caption grey--text mt-1 ml-6">
                        This value won't be changed
                      </div>
                      <v-btn 
                        small 
                        outlined
                        color="primary"
                        class="mt-2 ml-5"
                        @click="toggleIgnoreVariant(variant)"
                      >
                        <v-icon small left>mdi-undo</v-icon>
                        Include in options
                      </v-btn>
                    </div>
                    
                    <div v-else class="variant-option-content" @click="selectVariant(variant)">
                      <v-radio :value="variant.value" class="ma-0">
                        <template v-slot:label>
                          <div class="d-flex align-center flex-wrap">
                            <span class="variant-value mr-3">"{{ variant.value }}"</span>
                            <v-chip small outlined class="mr-2">
                              {{ variant.count }} row{{ variant.count > 1 ? 's' : '' }}
                            </v-chip>
                            <v-chip v-if="variant.value === currentIssue.suggestedValue" small color="success"
                              text-color="white">
                              Most common
                            </v-chip>
                          </div>
                        </template>
                      </v-radio>
                      <v-btn 
                        v-if="currentIssue.variants.length > 2 && activeVariantsCount > 2"
                        small 
                        outlined
                        color="primary"
                        class="keep-as-is-btn mt-2"
                        @click.stop="toggleIgnoreVariant(variant)"
                      >
                        <v-icon small left>mdi-check</v-icon>
                        Keep as-is
                      </v-btn>
                    </div>
                  </div>

                  <div class="variant-option"
                    :class="{ 'variant-option--selected': currentIssue.selectedValue === '__custom__' }"
                    @click="currentIssue.selectedValue = '__custom__'; onValueSelected()">
                    <v-radio value="__custom__" class="ma-0">
                      <template v-slot:label>
                        <span class="variant-value">Enter your own value</span>
                      </template>
                    </v-radio>
                  </div>
                </div>
              </v-radio-group>

              <v-text-field v-if="currentIssue.selectedValue === '__custom__'" v-model="currentIssue.customValue"
                label="Enter the value to use" :placeholder="'e.g., ' + currentIssue.suggestedValue" outlined
                class="mt-4" style="max-width: 500px;" @input="onCustomValueChange"></v-text-field>

              <v-alert v-if="getEffectiveValue(currentIssue) && activeVariantsCount >= 2" type="success" outlined class="mt-4">
                <div class="text-subtitle-2 mb-2">
                  <v-icon small class="mr-1">mdi-eye</v-icon>
                  What will change:
                </div>
                <div class="d-flex flex-wrap">
                  <template v-for="variant in activeVariants">
                    <div v-if="variant.value !== getEffectiveValue(currentIssue)" :key="variant.value"
                      class="change-item mr-4 mb-2">
                      <span class="old-value">"{{ variant.value }}"</span>
                      <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                      <span class="new-value">"{{ getEffectiveValue(currentIssue) }}"</span>
                      <span class="grey--text ml-1">({{ variant.count }} row{{ variant.count > 1 ? 's' : '' }})</span>
                    </div>
                  </template>
                </div>
              </v-alert>
            </div>

            <div class="harmonisation-action-footer">
              <v-btn outlined color="primary" @click="skipCurrent">
                Skip this one
              </v-btn>
              <v-btn v-if="currentIssue.selectedValue" text color="grey" @click="clearSelection">
                <v-icon small left>mdi-undo</v-icon>
                Clear selection
              </v-btn>
              <v-spacer></v-spacer>
              <div class="action-hint" v-if="!currentIssue.selectedValue">
                <v-icon small class="mr-1">mdi-information-outline</v-icon>
                Choose an option above, then click the button to continue
              </div>
              <v-btn color="primary" :disabled="!canApplyCurrent" @click="applyCurrent" large>
                {{ isLastIssue ? 'Continue' : 'Next' }}
                <v-icon right>mdi-chevron-right</v-icon>
              </v-btn>
            </div>

            <v-alert v-if="allResolved" :type="allResolved ? 'success' : 'info'" class="mt-4" outlined>
                All naming variations have been resolved. You can continue.
            </v-alert>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
  name: 'StepValueHarmonisation',
  props: {
    issues: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentIssueIndex: 0
    };
  },
  computed: {
    currentIssue() {
      return this.issues[this.currentIssueIndex] || null;
    },
    allResolved() {
      return this.issues.length > 0 && this.issues.every(i => i.resolved);
    },
    resolvedCount() {
      return this.issues.filter(i => i.resolved).length;
    },
    canApplyCurrent() {
      if (!this.currentIssue) return false;
      // Need at least 2 active (non-ignored) variants to make a choice
      if (this.activeVariantsCount < 2) return false;
      if (this.currentIssue.selectedValue === '__custom__') {
        return this.currentIssue.customValue && this.currentIssue.customValue.trim().length > 0;
      }
      // Selected value must not be ignored
      if (this.isVariantIgnoredByValue(this.currentIssue.selectedValue)) return false;
      return this.currentIssue.selectedValue !== null;
    },
    isLastIssue() {
      return this.currentIssueIndex === this.issues.length - 1;
    },
    activeVariantsCount() {
      if (!this.currentIssue) return 0;
      const ignored = this.currentIssue.ignoredVariants || [];
      return this.currentIssue.variants.filter(v => !ignored.includes(v.value)).length;
    },
    activeVariants() {
      if (!this.currentIssue) return [];
      const ignored = this.currentIssue.ignoredVariants || [];
      return this.currentIssue.variants.filter(v => !ignored.includes(v.value));
    }
  },
  methods: {
    getEffectiveValue(issue) {
      if (!issue) return null;
      if (issue.selectedValue === '__custom__') {
        return issue.customValue || null;
      }
      return issue.selectedValue;
    },
    getIssueChipColor(issue, idx) {
      if (idx === this.currentIssueIndex) return 'primary';
      if (issue.resolved) return 'success';
      return 'grey lighten-2';
    },
    goToIssue(idx) {
      this.currentIssueIndex = idx;
    },
    goToPrevious() {
      if (this.currentIssueIndex > 0) {
        this.currentIssueIndex--;
      }
    },
    goToNext() {
      if (this.currentIssueIndex < this.issues.length - 1) {
        this.currentIssueIndex++;
      }
    },
    onValueSelected() {
      if (this.currentIssue.selectedValue !== '__custom__') {
        this.currentIssue.customValue = '';
      }
    },
    selectVariant(variant) {
      if (this.isVariantIgnored(variant)) return;
      this.currentIssue.selectedValue = variant.value;
      this.onValueSelected();
    },
    isVariantIgnored(variant) {
      if (!this.currentIssue) return false;
      const ignored = this.currentIssue.ignoredVariants || [];
      return ignored.includes(variant.value);
    },
    isVariantIgnoredByValue(value) {
      if (!this.currentIssue || !value) return false;
      const ignored = this.currentIssue.ignoredVariants || [];
      return ignored.includes(value);
    },
    toggleIgnoreVariant(variant) {
      if (!this.currentIssue) return;
      
      // Initialize ignoredVariants array if needed
      if (!this.currentIssue.ignoredVariants) {
        this.$set(this.currentIssue, 'ignoredVariants', []);
      }
      
      const idx = this.currentIssue.ignoredVariants.indexOf(variant.value);
      if (idx === -1) {
        // Ignore this variant
        this.currentIssue.ignoredVariants.push(variant.value);
        
        // If selected value was just ignored, clear selection
        if (this.currentIssue.selectedValue === variant.value) {
          this.currentIssue.selectedValue = null;
        }
      } else {
        // Un-ignore this variant
        this.currentIssue.ignoredVariants.splice(idx, 1);
      }
      
      this.$emit('update:issues', [...this.issues]);
    },
    onCustomValueChange() {
      this.$emit('update:issues', [...this.issues]);
    },
    applyCurrent() {
      if (!this.currentIssue) return;
      this.currentIssue.resolved = true;
      this.$emit('update:issues', [...this.issues]);
      this.$emit('issue-resolved', {
        issue: this.currentIssue,
        value: this.getEffectiveValue(this.currentIssue)
      });

      if (this.allResolved) {
        this.$nextTick(() => {
          this.$emit('all-resolved');
        });
      } else {
        const nextUnresolved = this.issues.findIndex((i, idx) => idx > this.currentIssueIndex && !i.resolved);
        if (nextUnresolved !== -1) {
          this.currentIssueIndex = nextUnresolved;
        } else if (this.currentIssueIndex < this.issues.length - 1) {
          this.currentIssueIndex++;
        }
      }
    },
    clearSelection() {
      if (!this.currentIssue) return;
      this.currentIssue.resolved = false;
      this.currentIssue.selectedValue = null;
      this.currentIssue.customValue = '';
      if (this.currentIssue.ignoredVariants) {
        this.currentIssue.ignoredVariants.splice(0);
      }
      this.$emit('update:issues', [...this.issues]);
    },
    skipCurrent() {
      if (!this.currentIssue) return;
      this.currentIssue.resolved = true;
      this.currentIssue.skipped = true;
      this.currentIssue.selectedValue = null;
      this.currentIssue.customValue = '';
      if (this.currentIssue.ignoredVariants) {
        this.currentIssue.ignoredVariants.splice(0);
      }
      this.$emit('update:issues', [...this.issues]);

      if (this.currentIssueIndex < this.issues.length - 1) {
        this.currentIssueIndex++;
      }
    },
    formatRows(rows) {
      if (!rows || rows.length === 0) return '';
      if (rows.length <= 5) {
        if (rows.length === 1) return String(rows[0]);
        if (rows.length === 2) return rows[0] + ' and ' + rows[1];
        const allButLast = rows.slice(0, -1).join(', ');
        return allButLast + ', and ' + rows[rows.length - 1];
      }
      // Show first 4 and "and X more"
      return rows.slice(0, 4).join(', ') + ', and ' + (rows.length - 4) + ' more';
    }
  }
};
</script>

<style scoped>
.variants-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

@media (max-width: 960px) {
  .variants-grid {
    grid-template-columns: 1fr;
  }
}

.variant-option {
  position: relative;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 16px;
  transition: all 0.2s ease;
}

.variant-option-content {
  cursor: pointer;
}

.variant-option:hover:not(.variant-option--ignored) {
  border-color: #bbdefb;
  background-color: #f5f9ff;
}

.variant-option--selected {
  border-color: var(--v-primary-base, #1976d2);
  background-color: #e3f2fd;
}

.variant-option--ignored {
  border-color: #e0e0e0;
  background-color: #fafafa;
  border-style: dashed;
}

.variant-ignored-content {
  padding: 4px 0;
}

.variant-value {
  font-weight: 500;
  font-size: 16px;
}

.variant-value--ignored {
  color: #9e9e9e;
  text-decoration: line-through;
}

.keep-as-is-btn {
  margin-left: 28px;
}

.change-item {
  font-size: 14px;
}

.old-value {
  color: #c62828;
  text-decoration: line-through;
}

.new-value {
  color: #2e7d32;
  font-weight: 500;
}

.harmonisation-action-footer {
  position: sticky;
  bottom: 0;
  background: white;
  padding: 16px 0;
  margin-top: 16px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 10;
}

.action-hint {
  color: #757575;
  font-size: 13px;
  display: flex;
  align-items: center;
}
</style>
